import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { HelpCircle, ChevronRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

/* Aide / FAQ — Iter43. Petites bulles repliables pour ne pas surcharger la page. */
const SECTIONS = {
  client: [
    {
      id: "client-search",
      icon: "🔎",
      title: "Trouver un coach près de chez moi",
      body: (
        <>
          <p>Depuis la page <Link to="/map" className="text-sky-700 underline">Carte</Link>, vous pouvez :</p>
          <ul className="list-disc ml-5 mt-2 space-y-1 text-sm">
            <li>Saisir votre adresse pour centrer la carte sur votre quartier.</li>
            <li>Filtrer par tarif horaire, niveau, âge (enfant/adulte) et type de cours.</li>
            <li>Cliquer sur un marqueur pour voir le profil détaillé du maître-nageur (MNS) et ses créneaux disponibles.</li>
            <li>Ajouter le MNS à vos <Link to="/favorites" className="text-sky-700 underline">favoris</Link> pour le retrouver facilement.</li>
          </ul>
        </>
      ),
    },
    {
      id: "client-book",
      icon: "📅",
      title: "Réserver un cours",
      body: (
        <>
          <p>Sur la page d'un MNS :</p>
          <ol className="list-decimal ml-5 mt-2 space-y-1 text-sm">
            <li>Choisissez un créneau disponible (sélectionnez âge & niveau).</li>
            <li>Sélectionnez le mode de paiement : <strong>en ligne</strong> (carte) ou <strong>espèces</strong> (à régler au coach sur place).</li>
            <li>Ajoutez au <Link to="/cart" className="text-sky-700 underline">panier</Link> puis validez. Vous serez redirigé vers Stripe pour le paiement sécurisé.</li>
            <li>Votre carte sera enregistrée comme garantie d'annulation (sans débit additionnel).</li>
          </ol>
        </>
      ),
    },
    {
      id: "client-cancel",
      icon: "⚠️",
      title: "Politique d'annulation (importante !)",
      body: (
        <>
          <p>Annulation <strong>gratuite</strong> jusqu'à <strong>24 heures avant</strong> le cours.</p>
          <p className="mt-2">Toute annulation moins de 24 h avant le cours, ou tout <em>no-show</em> (vous ne venez pas) déclaré par le coach, entraîne la facturation automatique de <strong>30 % du tarif</strong> sur votre carte enregistrée. Si vous aviez payé en ligne, 70 % vous sont remboursés (30 % conservés au titre de la pénalité).</p>
        </>
      ),
    },
    {
      id: "client-payment",
      icon: "💳",
      title: "Paiement, facture & remboursement",
      body: (
        <>
          <ul className="list-disc ml-5 space-y-1 text-sm">
            <li>Vous payez par carte (Stripe) ou en espèces directement au coach (au choix par créneau).</li>
            <li>Une <strong>facture PDF</strong> conforme (Factur-X) est générée après chaque cours réalisé — téléchargeable depuis votre <Link to="/dashboard" className="text-sky-700 underline">tableau de bord</Link>.</li>
            <li>Si le coach annule, le remboursement est automatique (3-5 j ouvrés sur la carte d'origine).</li>
          </ul>
        </>
      ),
    },
    {
      id: "client-referral",
      icon: "🎁",
      title: "Parrainage & crédits",
      body: (
        <>
          <p>Invitez vos amis avec votre code parrainage (dans <Link to="/profile" className="text-sky-700 underline">Mon profil</Link>) : vous gagnez <strong>10 crédits</strong> à leur 1ʳᵉ réservation. Utilisez vos crédits au paiement pour obtenir des frais de service offerts ou une réduction directe.</p>
        </>
      ),
    },
    {
      id: "client-message",
      icon: "💬",
      title: "Communiquer avec mon coach",
      body: (
        <p>Une fois votre réservation payée, vous pouvez ouvrir la <Link to="/messages" className="text-sky-700 underline">messagerie</Link> et échanger directement avec votre coach (questions, besoins spécifiques, etc.).</p>
      ),
    },
  ],
  mns: [
    {
      id: "mns-setup",
      icon: "🚀",
      title: "Démarrer (1ʳᵉ visite)",
      body: (
        <ol className="list-decimal ml-5 space-y-1 text-sm">
          <li>Complétez votre <Link to="/mns/profile" className="text-sky-700 underline">profil MNS</Link> : prénom/nom, bio, tarif horaire, établissements rattachés, SIRET, TVA.</li>
          <li>Téléversez vos diplômes + attestation d'assurance dans la section <strong>📄 Documents officiels</strong> (repliable). Ces documents sont obligatoires pour publier des créneaux.</li>
          <li>Connectez votre compte bancaire via <strong>Stripe Connect</strong> (page Profil → Versements) pour recevoir vos paiements automatiquement.</li>
        </ol>
      ),
    },
    {
      id: "mns-slots",
      icon: "📆",
      title: "Publier mes créneaux",
      body: (
        <>
          <p>Dans <Link to="/mns/dashboard" className="text-sky-700 underline">Tableau de bord</Link> → onglet <strong>Planning</strong> :</p>
          <ol className="list-decimal ml-5 mt-2 space-y-1 text-sm">
            <li>Choisissez une piscine, une date et une plage horaire.</li>
            <li>Définissez le nombre max d'élèves par créneau, le tarif (si différent de votre tarif horaire) et le mode "niveaux mixtes".</li>
            <li>Cliquez sur <strong>Dupliquer la semaine</strong> pour générer plusieurs semaines en un clic.</li>
          </ol>
        </>
      ),
    },
    {
      id: "mns-hidden",
      icon: "👁‍🗨",
      title: "Créneaux cachés (révélation progressive)",
      body: (
        <>
          <p>Vous pouvez cacher certains créneaux d'une journée — ils n'apparaîtront aux clients qu'au fur et à mesure que les précédents seront réservés.</p>
          <p className="mt-2 text-sm">Sur l'onglet <strong>Calendrier</strong> de votre dashboard → cliquez sur un jour → bouton <strong>👁‍🗨 Cacher</strong> sur chaque créneau que vous voulez révéler progressivement.</p>
          <p className="mt-2 text-xs text-slate-500">Tolérance : dès qu'au moins un des créneaux visibles précédents a une réservation (même partielle), le créneau caché suivant devient visible automatiquement.</p>
        </>
      ),
    },
    {
      id: "mns-cancel",
      icon: "🛑",
      title: "Annuler des créneaux",
      body: (
        <>
          <p>Onglet <strong>Calendrier</strong> → cliquez sur un jour → cochez les créneaux à annuler → bouton <strong>Annuler la sélection</strong>.</p>
          <p className="mt-2 text-sm">Un <strong>récapitulatif "panier"</strong> s'ouvre avec tous les créneaux cochés — vous pouvez en décocher avant de confirmer. Les clients sont notifiés et remboursés automatiquement (paiements en ligne).</p>
        </>
      ),
    },
    {
      id: "mns-noshow",
      icon: "⚠️",
      title: "No-show & annulation tardive d'un client",
      body: (
        <>
          <p>Si un client ne se présente pas : ouvrez la réservation depuis votre dashboard et cliquez sur <strong>No-show</strong>.</p>
          <p className="mt-2 text-sm">Une pénalité de <strong>30 % du tarif</strong> est automatiquement prélevée sur sa carte enregistrée. Vous recevez <strong>90 %</strong> de cette pénalité dans votre cagnotte (10 % plateforme).</p>
        </>
      ),
    },
    {
      id: "mns-payouts",
      icon: "💰",
      title: "Recevoir mes paiements (Stripe Connect)",
      body: (
        <>
          <p>Profil → <strong>Versements</strong> :</p>
          <ul className="list-disc ml-5 space-y-1 text-sm mt-1">
            <li>Liez votre compte bancaire (IBAN) via Stripe Connect Express.</li>
            <li>Vos paiements sont automatiquement transférés à chaque cours validé (90 % du tarif + 90 % des pénalités).</li>
            <li>Demandez un versement quand vous voulez depuis votre solde.</li>
          </ul>
        </>
      ),
    },
    {
      id: "mns-invoice",
      icon: "🧾",
      title: "Factures & relevé mensuel",
      body: (
        <>
          <p>Chaque cours validé génère automatiquement une <strong>facture Factur-X</strong> conforme à la réglementation 2026.</p>
          <p className="mt-2 text-sm">Dans le dashboard onglet <strong>Comptabilité</strong>, vous pouvez télécharger un relevé mensuel (PDF + CSV) pour votre comptable.</p>
        </>
      ),
    },
  ],
  general: [
    {
      id: "gen-install",
      icon: "📱",
      title: "Installer l'application sur mon téléphone",
      body: (
        <>
          <p>L'app est installable comme une vraie application :</p>
          <ul className="list-disc ml-5 space-y-1 text-sm mt-1">
            <li><strong>iPhone (Safari)</strong> : appuyez sur <em>Partager</em> ↗ → <em>Sur l'écran d'accueil</em>.</li>
            <li><strong>Android (Chrome)</strong> : menu ⋮ → <em>Installer l'application</em>.</li>
            <li><strong>Ordinateur</strong> : cliquez sur le logo en bas de la page → <em>Installer</em>.</li>
          </ul>
        </>
      ),
    },
    {
      id: "gen-account",
      icon: "👤",
      title: "Gérer mon compte / mes données",
      body: (
        <ul className="list-disc ml-5 space-y-1 text-sm">
          <li><strong>Email, téléphone, civilité</strong> : <Link to="/profile" className="text-sky-700 underline">Mon profil</Link>.</li>
          <li><strong>Mot de passe</strong> : Mon profil → onglet Compte → Modifier le mot de passe.</li>
          <li><strong>Supprimer mon compte</strong> (RGPD) : Mon profil → onglet Compte → Supprimer mon compte (irréversible).</li>
          <li><strong>Rester connecté 30 jours</strong> : cochez la case sur la page de connexion.</li>
        </ul>
      ),
    },
    {
      id: "gen-contact",
      icon: "✉️",
      title: "Contacter le support",
      body: (
        <>
          <p>Pour toute question (bug, facturation, demande RGPD) :</p>
          <ul className="list-disc ml-5 space-y-1 text-sm mt-1">
            <li>Page <Link to="/contact" className="text-sky-700 underline">Contact</Link> — formulaire en ligne.</li>
            <li>Email direct : <a href="mailto:support@myswimminglessons.fr" className="text-sky-700 underline">support@myswimminglessons.fr</a></li>
            <li>Pour les questions juridiques : <Link to="/legal/cgu" className="text-sky-700 underline">CGU</Link>, <Link to="/legal/cgv" className="text-sky-700 underline">CGV</Link>, <Link to="/legal/privacy" className="text-sky-700 underline">Politique de confidentialité</Link>.</li>
          </ul>
        </>
      ),
    },
  ],
};

function FaqItem({ s }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.target.open)}
      className="card-soft p-4 group"
      data-testid={`faq-${s.id}`}
    >
      <summary className="cursor-pointer list-none flex items-start justify-between gap-3 select-none">
        <div className="flex items-start gap-3">
          <span className="text-2xl shrink-0" aria-hidden>{s.icon}</span>
          <h3 className="font-semibold text-slate-900 mt-0.5" style={{ fontFamily: "Outfit" }}>{s.title}</h3>
        </div>
        <ChevronRight className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </summary>
      <div className="mt-3 pl-10 text-slate-700 text-sm leading-relaxed space-y-2">{s.body}</div>
    </details>
  );
}

export default function HelpPage() {
  const { user } = useAuth();
  const initialTab = user?.role === "mns" ? "mns" : "client";
  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="help-page">
      <div className="flex items-center gap-3 mb-2">
        <HelpCircle className="w-8 h-8 text-sky-700" />
        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Centre d'aide</h1>
      </div>
      <p className="text-slate-600 mb-6">
        Tout ce qu'il faut savoir pour bien utiliser MySwimmingLessons. Cliquez sur une question pour développer la réponse.
      </p>

      <Tabs defaultValue={initialTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="client" data-testid="help-tab-client">🧑‍🦱 Client</TabsTrigger>
          <TabsTrigger value="mns" data-testid="help-tab-mns">🏊 Maître-nageur</TabsTrigger>
          <TabsTrigger value="general" data-testid="help-tab-general">⚙️ Général</TabsTrigger>
        </TabsList>

        <TabsContent value="client" className="space-y-3">
          {SECTIONS.client.map((s) => <FaqItem key={s.id} s={s} />)}
        </TabsContent>
        <TabsContent value="mns" className="space-y-3">
          {SECTIONS.mns.map((s) => <FaqItem key={s.id} s={s} />)}
        </TabsContent>
        <TabsContent value="general" className="space-y-3">
          {SECTIONS.general.map((s) => <FaqItem key={s.id} s={s} />)}
        </TabsContent>
      </Tabs>

      <div className="mt-8 card-soft p-5 bg-sky-50 border border-sky-100">
        <h2 className="font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Vous ne trouvez pas votre réponse ?</h2>
        <p className="text-sm text-slate-700 mb-3">L'équipe support est à votre disposition.</p>
        <Link to="/contact" className="inline-block">
          <span className="inline-flex items-center gap-2 rounded-full bg-sky-700 hover:bg-sky-600 text-white px-4 py-2 text-sm font-medium" data-testid="help-contact-cta">
            Contacter le support →
          </span>
        </Link>
      </div>
    </div>
  );
}
